/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LeaseService } from '../services/lease-service';
import { leasesKeys } from './leases-keys';
import { markRescissionCancellations } from '../utils/lease-detail-derivations';
import type { LeaseAction } from '../constants/lease-states';
import type { Lease, LeaseExpectation } from '../types/lease.types';

/**
 * The optional body a transition carries.
 *
 * `cancel` needs `reason` — the backend refuses a blank one with 422 —
 * so the hook has to be able to send it even before any UI collects it.
 */
export interface LeaseTransitionBody {
  from_date?: string;
  to_date?: string;
  reason?: string;
}

export interface LeaseDetail {
  isLoading: boolean;
  isError: boolean;
  lease: Lease | null;
  expectations: LeaseExpectation[];
  applyTransition: (action: LeaseAction, body?: LeaseTransitionBody) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Loads one lease plus its competência panel, and exposes the lifecycle
 * transition.
 *
 * @param agencyId - The owning agency.
 * @param leaseId - The lease to load.
 * @returns The view-model: flags, the lease, its competências, and actions.
 */
export function useLease(agencyId: string, leaseId: string): LeaseDetail {
  const queryClient = useQueryClient();
  const enabled = Boolean(agencyId && leaseId);

  const leaseQuery = useQuery({
    queryKey: leasesKeys.detail(agencyId, leaseId),
    queryFn: () => LeaseService.getLease(agencyId, leaseId),
    enabled,
  });

  const expectationsQuery = useQuery({
    queryKey: leasesKeys.expectations(agencyId, leaseId),
    queryFn: () => LeaseService.listExpectations(agencyId, leaseId),
    enabled,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['leases', agencyId] });

  return {
    isLoading: leaseQuery.isLoading,
    isError: leaseQuery.isError,
    lease: leaseQuery.data ?? null,
    expectations: markRescissionCancellations(
      expectationsQuery.data?.data ?? [],
      leaseQuery.data?.closure?.cancelled_expectation_ids,
    ),
    applyTransition: async (action, body) => {
      const updated = await LeaseService.applyTransition(agencyId, leaseId, action, body);
      // The answer already carries the new state. Showing it now, and
      // holding the button until the refetch lands, closes the two seconds
      // in which the header still read "Rascunho" under a finished spinner
      // and offered the actions of a state the lease had already left.
      // The answer has no party names (only the GET adds them), so the
      // landlords already on screen are kept rather than blanked.
      queryClient.setQueryData<Lease>(
        leasesKeys.detail(agencyId, leaseId),
        (current) => (current
          ? { ...current, ...updated, landlords: current.landlords }
          : updated),
      );
      await refresh();
    },
    refresh,
  };
}
